import {
  Editor,
  Path,
  Transforms,
  Node,
  Range
} from 'slate';

import {
  ReactEditor
} from 'slate-react';

import elementTypes from '../../elements/types';

import HelptapEditor from '../';

export function isFromAndToSameType(expectedType, editor, from, to, caller) {

  /***
   * 
   * next is by default true. Meaning next rule will be checked.
   * next will be false, if the current block accepts or rejects the drop.
   * 
   * **/
  const result = { accept: false, next: true };

  const fromNodeEntry = Editor.above(editor, {
    at: from,
    match: n => n.type === expectedType
  });

  let toNodeEntry = null;

  if (Node.has(editor, to) === true) {
    toNodeEntry = Editor.above(editor, {
      at: to,
      match: n => n.type === expectedType
    });
  }

  const fromIsDataCollectionChild = Boolean(fromNodeEntry);
  const toIsDataCollectionChild = Boolean(toNodeEntry);

  result.next = fromIsDataCollectionChild === false && toIsDataCollectionChild === false;

  if (fromIsDataCollectionChild === true && toIsDataCollectionChild === true) {
    result.accept = Path.equals(fromNodeEntry[1], toNodeEntry[1]) === true;
  }

  return result;

}

function resolveGalleryPath(editor, location) {

  let currentPath = location;

  while (currentPath.length > 0) {

    if (Node.has(editor, currentPath) === true) {

      const node = Node.get(editor, currentPath);

      if (node.type === elementTypes.GALLERY) {
        return currentPath;
      }

    }

    currentPath = Path.parent(currentPath);

  }

  return null;

}

export function isWithinSameGallery(editor, from, to) {

  const result = { accept: false, next: true };

  const fromGalleryEntry = Editor.above(editor, {
    at: from,
    match: n => n.type === elementTypes.GALLERY
  });

  let fromGalleryPath = null;

  if (fromGalleryEntry) {
    fromGalleryPath = fromGalleryEntry[1];
  }

  const toGalleryPath = resolveGalleryPath(editor, to);

  const fromInGallery = fromGalleryPath !== null;
  const toInGallery = toGalleryPath !== null;

  result.next = fromInGallery === false && toInGallery === false;

  if (fromInGallery === true && toInGallery === true) {
    result.accept = Path.equals(fromGalleryPath, toGalleryPath) === true;
  }

  return result;

}

export function canReceiverAccept(editor, from, to, caller) {

  const rules = [
    () => isFromAndToSameType(elementTypes.DATA_COLLECTION_ROW, editor, from, to, caller),
    () => isFromAndToSameType(elementTypes.DATA_COLLECTION_ROW_PAGE, editor, from, to, caller),
    () => isWithinSameGallery(editor, from, to)
  ];

  for (let rule of rules) {

    const result = rule();

    if (result.accept === true) {
      return true;
    }

    if (result.next === false) {
      return false;
    }

  }

  return true;

}

function getFromPathReferences(editor, start, end) {

  HelptapEditor.ensureSamePathLevels(start, end);

  const selection = {
    anchor: {
      path: start,
      offset: 0
    },
    focus: {
      path: end,
      offset: 0
    }
  };

  const fromPathReferences = HelptapEditor.getNodePathsFromSelection(editor, selection, 'all')
    .filter(path => path.length === start.length)
    .map(path => Editor.pathRef(editor, path));

  return fromPathReferences;

}

export function getSameLevelPathReferences(editor, selection) {

  const start = [...HelptapEditor.removeTextNodeFromPath(editor, selection.anchor.path)];
  const end = [...HelptapEditor.removeTextNodeFromPath(editor, selection.focus.path)];

  HelptapEditor.ensureSamePathLevels(start, end);

  const fromPathReferences = getFromPathReferences(editor, start, end);

  return fromPathReferences;

}

function hasColumnNode(editor, source) {

  const [match] = Editor.nodes(editor, {
    at: source,
    match: n => n.type === elementTypes.COLUMNS,
  });

  return !!match;

}

function pathRefsToOrderedRange(editor, pathRefs) {

  const paths = pathRefs
    .filter((pathRef) => pathRef.current);

  if (paths.length < 2) {
    return [paths[0]];
  }

  paths.sort((a, b) => Path.compare(a.current, b.current));

  return paths;
}

export function tryMoveNodeV2(editor, event, editorLevelNode, elementPath, destination, data) {

  let fromPathReferences = getSameLevelPathReferences(editor, data.selection).sort((a, b) => Path.compare(a.current, b.current) * -1);

  if (fromPathReferences.length === 0) {
    return;
  }
  let paths = pathRefsToOrderedRange(editor, fromPathReferences);
  const toReference = Editor.pathRef(editor, destination);

  try {

    const accepted = canReceiverAccept(editor, fromPathReferences[0].current, toReference.current, 'onDrop');

    if (accepted === false) {
      return false;
    }

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(editor, fromPathReferences[0].current) === true) {
      HelptapEditor.dataCollection.reorder(editor, fromPathReferences[0].current, toReference.current);
      return false;
    }

    if (Path.compare(paths[0].current, toReference.current) === -1) {
      paths = paths.reverse();
    }

    for (let fromPath of paths) {

      Transforms.moveNodes(editor, {
        at: fromPath.current, // The range to move
        to: toReference.current, // The target path
      });

    }

  } catch (exception) {

    // eslint-disable-next-line no-console
    console.error(exception);

  } finally {
    for (let fromPathReference of fromPathReferences) {

      fromPathReference.unref();

    }

    toReference.unref();
  }

}

export function canSelectHandleNode(editor, elementPath) {

  const start = [...HelptapEditor.removeTextNodeFromPath(editor, editor.selection.anchor.path)];
  const end = [...HelptapEditor.removeTextNodeFromPath(editor, editor.selection.focus.path)];

  const fromPathReferences = getFromPathReferences(editor, start, end);

  for (let references of fromPathReferences) {

    const from = references.current;

    if (Path.equals(from, elementPath) === true) {
      return false;
    }

  }

  return true;

}